import React, { useCallback, useEffect, useState } from 'react'
import api from '../../../../../services/api'
import Form, {
  Input,
  Select,
  SelectSearch,
  Textarea
} from '../../../../../components/Form'
import { useHistory } from 'react-router-dom'
import { useToast } from '../../../../../hooks/toast'
import { useLoading } from '../../../../../hooks/loading'
import { useUpdateDataTable } from '../../../../../hooks/dataTable'
import { apiCreate } from '../../domain/api'
import {
  nameActions,
  ROUTE_PRIORITY_ENUM,
  ROUTE_PRIORITY_MAP,
  ROUTE_STATUS_ENUM
} from '../../domain/info'
import { FormContainer } from './styles'
import { INPUT_MASK } from '../../../../../common/constants'
import { apiListPromotersOptions } from '../../../Promoters/domain/api'
import { v4 } from 'uuid'
import { Date as DatePicker } from '../../../../../components/Form/date'
import { IMaskInput } from 'react-imask'
import moment from 'moment'
import { listAgenciesByPromoterOptions } from '../../../Agencies/domain/api'

type IsOpenInModalProps = {
  idParent: number
  handleOnClose: () => void
}

type RouteData = {
  id?: string
  promoter_id: number
  name: string
  scheduled_date: string
  priority: ROUTE_PRIORITY_ENUM
  observation?: string | null
  status: ROUTE_STATUS_ENUM
  completed_at?: Date | null
  routeAgencies: {
    id?: string | number
    route_id?: number
    agency_id?: number
    priority: ROUTE_PRIORITY_ENUM
    observation?: string | null
    show?: boolean
  }[]
}

type RouteResponseData = {
  route: {
    promoter_id: number
    priority: ROUTE_PRIORITY_ENUM
    observation?: string | null
    status: ROUTE_STATUS_ENUM
    completed_at?: Date | null
  }
  routeAgencies?: {
    id?: number | string
    agency_id?: number
    priority: ROUTE_PRIORITY_ENUM
    observation?: string | null
    show?: boolean
  }[]
  routesNames?: {
    id: number | string
    name: string
    scheduled_date?: Date
    show: boolean
  }[]
}

interface ICreateRouteWithRoutesAgenciesDTO {
  route: {
    promoter_id: number
    name: string
    scheduled_date: string
    priority: ROUTE_PRIORITY_ENUM
    observation?: string | null
  }
  routeAgencies: {
    agency_id: number
    priority: ROUTE_PRIORITY_ENUM
    observation?: string | null
  }[]
}

type TypesFormProps = {
  isOpenInModal?: false | IsOpenInModalProps
  initialValues?: RouteData
}

export const FormRoute = ({
  isOpenInModal,
  initialValues
}: TypesFormProps): JSX.Element => {
  const { addToast } = useToast()
  const history = useHistory()
  const { updateDataTable } = useUpdateDataTable()

  const [routesNames, setRouteNames] = useState<
    RouteResponseData['routesNames']
  >([])

  const [routeAgencies, setRouteAgencies] = useState<
    RouteResponseData['routeAgencies']
  >([])

  const [promotersOptions, setPromotersOptions] = useState([])
  const [agenciesOptions, setAgenciesOptions] = useState([])

  const [defaultValues, setDefaultValues] = useState<RouteResponseData>()

  const loadAgencies = useCallback(async (promoter_id: number) => {
    if (!promoter_id) return
    try {
      const responseAgencies = await api.get(
        listAgenciesByPromoterOptions(String(promoter_id))
      )
      setAgenciesOptions(
        responseAgencies.data?.map((item: { name: string; id: number }) => ({
          name: `${item.name}`,
          value: item.id
        }))
      )
    } catch (error) {}
  }, [])

  useEffect(() => {
    const routesNames: RouteResponseData['routesNames'] = [
      {
        id: v4(),
        name: '',
        show: true
      }
    ]
    setRouteNames(routesNames)

    const routeAgencies: RouteResponseData['routeAgencies'] = [
      {
        id: v4(),
        priority: ROUTE_PRIORITY_ENUM.LOW,
        show: true
      }
    ]
    setRouteAgencies(routeAgencies)

    if (initialValues) {
      setRouteAgencies(
        initialValues.routeAgencies.map(item => ({
          ...item,
          id: v4(),
          show: true
        }))
      )
      loadAgencies(initialValues.promoter_id)
      setDefaultValues({
        route: {
          priority: initialValues.priority,
          promoter_id: initialValues.promoter_id,
          status: initialValues.status,
          completed_at: initialValues.completed_at,
          observation: initialValues.observation
        },
        routesNames: [
          {
            id: v4(),
            name: initialValues.name,
            scheduled_date: moment(initialValues.scheduled_date).toDate(),
            show: true
          }
        ],
        routeAgencies: initialValues.routeAgencies
      })
    }
  }, [initialValues, loadAgencies])

  const { activeLoading, disableLoading } = useLoading()

  const onSubmitForm = async (data: RouteResponseData) => {
    data.routesNames = data.routesNames?.filter(item => item.name)

    data.routeAgencies = data.routeAgencies?.filter(item => item.agency_id)

    const dataCreate: ICreateRouteWithRoutesAgenciesDTO[] =
      data.routesNames.map(routeName => {
        return {
          route: {
            name: routeName.name,
            scheduled_date: routeName.scheduled_date.toISOString(),
            priority: data.route.priority,
            promoter_id: data.route.promoter_id,
            observation: data.route.observation
          },
          routeAgencies: data.routeAgencies.map(agency => ({
            agency_id: agency.agency_id,
            priority: agency.priority,
            observation: agency.observation
          }))
        }
      })

    try {
      try {
        activeLoading()
        await api.post(apiCreate(), dataCreate)
        disableLoading()
        updateDataTable()
        addToast({
          type: 'success',
          title: 'Registro criado',
          description: 'Registro criado com sucesso'
        })
        history.push(nameActions.read.to)
      } catch (error) {
        addToast({
          type: 'error',
          title: 'Erro ao adicionar o registro',
          description:
            'Ocorreu um erro ao fazer cadastro, por favor, tente novamente.'
        })
        disableLoading()
        updateDataTable()
      }
      disableLoading()
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro no cadastro',
        description:
          'Ocorreu um erro ao fazer cadastro, por favor, tente novamente.'
      })
      if (isOpenInModal) isOpenInModal.handleOnClose()
    }
  }

  const loadPromoters = useCallback(async () => {
    try {
      const responsePromoters = await api.get(apiListPromotersOptions())
      setPromotersOptions(
        responsePromoters.data?.map(
          (item: { name: string; cpf: string; id: number }) => ({
            name: `${item.cpf} ${item.name}`,
            value: item.id
          })
        )
      )
    } catch (error) {}
  }, [])

  useEffect(() => {
    loadPromoters()
  }, [loadPromoters])

  const removeRouteName = (id: string | number) => {
    if (!routesNames.some(item => item.show === true && item.id !== id)) {
      return
    }
    setRouteNames(prev =>
      prev.map(item => ({ ...item, show: item.id === id ? false : item.show }))
    )
  }

  const addRouteName = () => {
    setRouteNames(prev => [
      ...prev,
      {
        id: v4(),
        name: '',
        show: true
      }
    ])
  }

  const removeRouteAgency = (id: string | number) => {
    setRouteAgencies(prev =>
      prev.map(item => ({ ...item, show: item.id === id ? false : item.show }))
    )
  }

  const addRouteAgency = () => {
    setRouteAgencies(prev => [
      ...prev,
      {
        id: v4(),
        priority: ROUTE_PRIORITY_ENUM.LOW,
        show: true
      }
    ])
  }

  const setRouteNameScheduledDate = (id: string | number, date: Date) => {
    setRouteNames(prev =>
      prev.map(item => ({
        ...item,
        scheduled_date: item.id === id ? date : item.scheduled_date
      }))
    )
  }

  return (
    <Form onSubmit={onSubmitForm} defaultValues={defaultValues}>
      <>
        <div className="card mb-5 mb-xl-10">
          <FormContainer className="form">
            <div className="row mb-5">
              <SelectSearch
                name="route.promoter_id"
                label="Promotor"
                className="col-md-4"
                options={promotersOptions}
                blank
                defaultValue=""
                controlled
                placeholder="Digite para buscar promotor..."
                noOptionsMessage="Nenhum promotor encontrado"
                rules={{ required: true }}
                onSelectCallback={option => {
                  if (option?.value) {
                    setDefaultValues(prev => {
                      return {
                        route: {
                          ...prev?.route,
                          promoter_id: Number(option.value)
                        },
                        routeAgencies: [
                          {
                            priority: ROUTE_PRIORITY_ENUM.LOW,
                            show: true
                          }
                        ]
                      }
                    })
                    setRouteAgencies([
                      {
                        id: v4(),
                        priority: ROUTE_PRIORITY_ENUM.LOW,
                        show: true
                      }
                    ])
                    loadAgencies(Number(option.value))
                  }
                }}
              />
              <Select
                name="route.priority"
                label="Prioridade"
                rules={{ required: true }}
                className="col-md-4"
                options={Object.entries(ROUTE_PRIORITY_MAP).map(
                  ([key, label]) => ({
                    name: label,
                    value: key
                  })
                )}
                controlled
                blank
              />
              <Textarea
                name="route.observation"
                label="Observações da rota"
                className="col-md-8"
              />
            </div>

            <h4 className="pt-4">Rotas</h4>
            {routesNames.map(
              (routeName, index) =>
                routeName.show && (
                  <div className="row mb-5" key={routeName.id}>
                    <Input
                      name={`routesNames.${index}.name`}
                      label="Nome"
                      rules={{ required: true }}
                      className="col-md-4"
                      controlled
                    />
                    <DatePicker
                      name={`routesNames.${index}.scheduled_date`}
                      className="col-md-4"
                      label="Data"
                      dateFormat="dd/MM/yyyy"
                      placeholderText="DD/MM/AAAA"
                      customInput={
                        <IMaskInput
                          mask={Date}
                          pattern={'d/m/Y'}
                          format={date => {
                            return moment(date).format(INPUT_MASK.DATE_LOCALE)
                          }}
                          parse={value => {
                            return moment(
                              value,
                              INPUT_MASK.DATE_LOCALE
                            ).toDate()
                          }}
                        />
                      }
                      onChange={value =>
                        setRouteNameScheduledDate(routeName.id, value)
                      }
                      selected={routeName.scheduled_date}
                      controlled
                      rules={{ required: true }}
                    />

                    <div
                      className="col-md-1 d-flex align-items-end"
                      style={{ marginBottom: '18px' }}
                    >
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => removeRouteName(routeName.id)}
                      >
                        Excluir
                      </button>
                    </div>
                  </div>
                )
            )}
            <div className="d-flex mb-5">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => addRouteName()}
              >
                Adicionar rota
              </button>
            </div>

            <h4 className="pt-4">Agências</h4>
            {routeAgencies.map(
              (routeAgency, index) =>
                routeAgency.show && (
                  <div className="row mb-5" key={routeAgency.id}>
                    <SelectSearch
                      name={`routeAgencies.${index}.agency_id`}
                      label="Agência"
                      className="col-md-4"
                      options={agenciesOptions}
                      blank
                      defaultValue=""
                      placeholder="Digite para buscar agência..."
                      noOptionsMessage="Nenhuma agência encontrada"
                      controlled
                    />
                    <Select
                      name={`routeAgencies.${index}.priority`}
                      label="Prioridade"
                      className="col-md-4"
                      options={Object.entries(ROUTE_PRIORITY_MAP).map(
                        ([key, label]) => ({
                          name: label,
                          value: key
                        })
                      )}
                      controlled
                      blank
                    />

                    <Textarea
                      name={`routeAgencies.${index}.observation`}
                      label="Observações"
                      className="col-md-8"
                    />

                    <div
                      className="col-md-1 d-flex align-items-end"
                      style={{ marginBottom: '74px' }}
                    >
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => removeRouteAgency(routeAgency.id)}
                      >
                        Excluir
                      </button>
                    </div>
                  </div>
                )
            )}
            <div className="d-flex mb-5">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => addRouteAgency()}
              >
                Adicionar Agência
              </button>
            </div>
          </FormContainer>
          <div className="card-footer d-flex justify-content-end py-6 px-9">
            <button type="submit" className="btn btn-primary">
              Salvar
            </button>
          </div>
        </div>
      </>
    </Form>
  )
}
